/**
 * Phase 32 Plan 32-3 — TanStack Query hooks for engineering value tags.
 *
 * Each mutation invalidates BOTH `['value-tags', userId]` (the local list)
 * AND `['engineering-metrics', 'engineer', userId]` (Plan 32-2's drill-down
 * cache) so a newly-tagged engineer's drill-down page reflects the change
 * without a manual refetch.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchValueTagsForUser,
  createValueTag,
  updateValueTag,
  deleteValueTag,
  type CreateValueTagInput,
  type UpdateValueTagInput,
  type ValueTag,
} from '../services/api/valueTags';

export function useValueTagsForUser(
  userId: number | undefined,
  opts?: { projectId?: number },
) {
  return useQuery({
    queryKey: ['value-tags', userId, opts?.projectId ?? null] as const,
    queryFn: () => fetchValueTagsForUser(userId as number, opts),
    enabled: typeof userId === 'number' && Number.isFinite(userId) && userId > 0,
  });
}

export function useCreateValueTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      input,
    }: {
      projectId: number;
      input: CreateValueTagInput;
    }) => createValueTag(projectId, input),
    onSuccess: (tag: ValueTag) => {
      qc.invalidateQueries({ queryKey: ['value-tags', tag.userId] });
      qc.invalidateQueries({ queryKey: ['engineering-metrics', 'engineer', tag.userId] });
    },
  });
}

export function useUpdateValueTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateValueTagInput }) =>
      updateValueTag(id, input),
    onSuccess: (tag: ValueTag) => {
      qc.invalidateQueries({ queryKey: ['value-tags', tag.userId] });
      qc.invalidateQueries({ queryKey: ['engineering-metrics', 'engineer', tag.userId] });
    },
  });
}

export function useDeleteValueTag(userIdHint?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteValueTag(id),
    onSuccess: () => {
      if (userIdHint) {
        qc.invalidateQueries({ queryKey: ['value-tags', userIdHint] });
        qc.invalidateQueries({ queryKey: ['engineering-metrics', 'engineer', userIdHint] });
      } else {
        qc.invalidateQueries({ queryKey: ['value-tags'] });
        qc.invalidateQueries({ queryKey: ['engineering-metrics'] });
      }
    },
  });
}
